import type { DeliveryJob, DeliveryStatus, LegStatus, RiderProfile, RiderStatus } from '@/api/types';

/**
 * Everything this app knows about where a delivery has got to.
 *
 * One file, because the answer has to be the same in the tab badge, the run
 * sheet, the detail screen and the action bar. Six files each deciding what
 * "in hand" means is how an address block renders empty and gets filed as a
 * rendering bug.
 */

/**
 * Written from the RIDER's side of it.
 *
 * The customer app says "Picked up"; here the same state is "In your bag",
 * because the two are reading about different halves of one event.
 */
const STATUS_LABELS: Record<DeliveryStatus, string> = {
  PENDING: 'Waiting for a rider',
  ASSIGNED: 'New job',
  ACCEPTED: 'Head to the pharmacy',
  ARRIVED_AT_PHARMACY: 'At the pharmacy',
  PICKED_UP: 'In your bag',
  OUT_FOR_DELIVERY: 'On the way',
  ARRIVED_AT_CUSTOMER: 'At the door',
  DELIVERED: 'Delivered',
  FAILED: 'Could not deliver',
  CANCELLED: 'Cancelled',
};

export function statusLabel(status: DeliveryStatus): string {
  return STATUS_LABELS[status] ?? 'Unknown';
}

export type StatusTone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger';

/**
 * Colour is never the only marker — every pill that uses this also carries an
 * icon and the words above. This decides emphasis, not meaning.
 */
export function statusTone(status: DeliveryStatus): StatusTone {
  switch (status) {
    case 'DELIVERED':
      return 'success';
    case 'FAILED':
      return 'danger';
    case 'CANCELLED':
      return 'neutral';
    case 'ASSIGNED':
      return 'warning';
    case 'PENDING':
      return 'neutral';
    default:
      return 'brand';
  }
}

export function isTerminal(status: DeliveryStatus): boolean {
  return status === 'DELIVERED' || status === 'FAILED' || status === 'CANCELLED';
}

/**
 * Whether the medicine is physically in the rider's bag.
 *
 * Mirrors the backend's `DELIVERY_IN_HAND` exactly, and the two must stay in
 * step: it is what decides whether the customer's address and phone number are
 * in the payload at all, so a UI that guessed differently would draw an empty
 * address block and call it a bug.
 */
export function isInHand(status: DeliveryStatus): boolean {
  return status === 'PICKED_UP' || status === 'OUT_FOR_DELIVERY' || status === 'ARRIVED_AT_CUSTOMER';
}

export function isLive(job: DeliveryJob): boolean {
  return !isTerminal(job.status);
}

/**
 * Cash actually owed at this door.
 *
 * Both halves matter: a prepaid order carries the same `codAmount` on the wire
 * and nothing is owed on it, and a COD order the customer paid online before
 * the rider arrived is settled too. Asking for money on either is the kind of
 * mistake a customer remembers.
 */
export function isCashOnDelivery(job: DeliveryJob): boolean {
  return job.paymentMethod === 'COD' && job.paymentStatus !== 'PAID';
}

/** The bag is packed and on the counter. Pickup 409s until this is true. */
export function isReadyToCollect(job: DeliveryJob): boolean {
  return job.legStatus === 'READY_FOR_PICKUP';
}

const LEG_LABELS: Record<LegStatus, string> = {
  PENDING: 'The shop has not started on it',
  ACCEPTED: 'Accepted, not packed yet',
  REJECTED: 'The shop turned this order down',
  PICKING: 'Being picked off the shelves now',
  /*
   * Packed is NOT ready. The shop has filled the bag but not put it on the
   * counter, and pickup is refused until it does — so this says "nearly",
   * which is the difference between a rider waiting two minutes and a rider
   * tapping a button that answers 409.
   */
  PACKED: 'Packed — not on the counter yet',
  READY_FOR_PICKUP: 'Packed and waiting for you',
  HANDED_OVER: 'Collected',
  CANCELLED: 'This order was cancelled',
};

/**
 * What the pharmacy is doing, said only while it still matters.
 *
 * Once the bag is in hand the shop's state is history, and a line about it
 * under the address is one more thing to read past at a doorway.
 */
export function legHint(job: DeliveryJob): string | null {
  if (isInHand(job.status) || isTerminal(job.status)) return null;
  return LEG_LABELS[job.legStatus ?? 'PENDING'] ?? 'Not packed yet — the shop still has it';
}

/**
 * The four things a rider does on every job, in order.
 *
 * Deliberately four and not the seven statuses: "at the pharmacy" and "in
 * your bag" are not steps a rider thinks in, they are moments inside one. A
 * counter that says "Step 3 of 4" is something anyone can read; a status name
 * has to be learned.
 */
export const JOB_STEPS = ['Accept', 'Go to shop', 'Collect', 'Deliver'] as const;

/** Which of `JOB_STEPS` the rider is on, 0-based. Terminal jobs are past the end. */
export function jobStep(status: DeliveryStatus): number {
  switch (status) {
    case 'PENDING':
    case 'ASSIGNED':
      return 0;
    case 'ACCEPTED':
      return 1;
    case 'ARRIVED_AT_PHARMACY':
      return 2;
    case 'PICKED_UP':
    case 'OUT_FOR_DELIVERY':
    case 'ARRIVED_AT_CUSTOMER':
      return 3;
    default:
      return JOB_STEPS.length;
  }
}

/**
 * What to do next, as an instruction rather than a state.
 *
 * "At the pharmacy" tells a rider where the app thinks they are; "Collect the
 * bag" tells them what to do, which is the thing someone unsure of the app
 * actually needs. Read the leg as well as the delivery, so a rider at a
 * counter with nothing on it is told to wait rather than to collect.
 */
export function nextStep(job: DeliveryJob): string {
  switch (job.status) {
    case 'ASSIGNED':
      return 'Open it and accept';
    case 'ACCEPTED':
      return 'Ride to the pharmacy';
    case 'ARRIVED_AT_PHARMACY':
      return isReadyToCollect(job) ? 'Collect the bag' : 'Wait — the shop is still packing';
    case 'PICKED_UP':
      return 'Start the ride to the customer';
    case 'OUT_FOR_DELIVERY':
      return 'Ride to the customer';
    case 'ARRIVED_AT_CUSTOMER':
      return 'Hand it over and take the code';
    default:
      return statusLabel(job.status);
  }
}

/*
 * How far along a job is, as a sortable number.
 *
 * The server orders deliveries newest-first, which is right for a dispatch
 * board and wrong for a rider: the job that matters is the one already in the
 * bag, and a fresh assignment arriving would otherwise push it down the screen
 * mid-delivery.
 */
const STAGE: Record<DeliveryStatus, number> = {
  ARRIVED_AT_CUSTOMER: 6,
  OUT_FOR_DELIVERY: 5,
  PICKED_UP: 4,
  ARRIVED_AT_PHARMACY: 3,
  ACCEPTED: 2,
  ASSIGNED: 1,
  PENDING: 0,
  DELIVERED: 0,
  FAILED: 0,
  CANCELLED: 0,
};

/**
 * The run sheet in the order a rider works it: furthest along first, then
 * oldest first among jobs at the same stage.
 *
 * Copies rather than sorting in place — the array belongs to the query cache,
 * and sorting it there would mutate what every other reader sees.
 */
export function inWorkingOrder(jobs: readonly DeliveryJob[]): DeliveryJob[] {
  return [...jobs].sort((a, b) => {
    const stage = (STAGE[b.status] ?? 0) - (STAGE[a.status] ?? 0);
    if (stage !== 0) return stage;
    return Date.parse(a.createdAt) - Date.parse(b.createdAt);
  });
}

/**
 * Whether the rider is physically holding somebody's medicine.
 *
 * Read by the shift switch and the sign-out flow, both of which have to refuse
 * politely rather than let a bag disappear off the dispatch board.
 */
export function isCarrying(jobs: readonly DeliveryJob[]): boolean {
  return jobs.some((job) => isInHand(job.status));
}

/** Where a rider is in their shift, in the words the screen uses. */
export function shiftLabel(rider: RiderProfile | null | undefined): string {
  const status: RiderStatus | undefined = rider?.status;
  switch (status) {
    case 'BUSY':
      return 'On a job';
    case 'AVAILABLE':
      return 'On shift';
    case 'SUSPENDED':
      return 'Suspended';
    default:
      return 'Off shift';
  }
}

export function shiftDetail(rider: RiderProfile | null | undefined): string {
  if (!rider) return '';
  if (!rider.isActive) return 'Your account has been deactivated by the office.';
  switch (rider.status) {
    case 'SUSPENDED':
      return 'Only the office can lift this.';
    case 'BUSY':
      return 'Set by the system while you are carrying an order, not by this switch.';
    case 'AVAILABLE':
      return 'The office can send you jobs.';
    default:
      return 'Nothing will be assigned to you.';
  }
}
